import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import type {
  SpecialtyBookPayload,
  SpecialtyOptionChoice,
  SpecialtyOptionField,
  SpecialtyOptionsResponse,
  SpecialtyParty,
  SpecialtyPiece,
  SpecialtyProvider,
} from '../../apis/specialtyBooking';
import type { SingleShipmentDataExtended } from '../../apis/types';
import {
  type ShellPartyValues,
  shellToQuoteFormValues,
} from './shellShipmentHelpers';

export const SPECIALTY_TRANSPORT_PREFIX = 'specialty:';

export function specialtyTransportValue(provider: string): string {
  return `${SPECIALTY_TRANSPORT_PREFIX}${provider}`;
}

export function parseSpecialtyTransportProvider(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (!value.startsWith(SPECIALTY_TRANSPORT_PREFIX)) return null;
  const key = value
    .slice(SPECIALTY_TRANSPORT_PREFIX.length)
    .trim()
    .toLowerCase();
  return key || null;
}

export function specialtyProviderOptions(
  providers: SpecialtyProvider[]
): Array<{ label: string; value: string }> {
  return providers.map((p) => ({
    label: p.title || p.provider,
    value: specialtyTransportValue(p.provider),
  }));
}

export interface SpecialtyFormParty {
  name?: string;
  company?: string;
  phone?: string;
  email?: string;
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  notes?: string;
}

export interface SpecialtyFormPiece {
  weight?: number | string;
  weightUnit?: 'LB' | 'KG';
  length?: number | string;
  width?: number | string;
  height?: number | string;
  dimensionUnit?: 'IN' | 'CM';
  quantity?: number | string;
}

export interface SpecialtyFormValues {
  specialty_shipper?: SpecialtyFormParty;
  specialty_recipient?: SpecialtyFormParty;
  specialty_ready_date?: Dayjs | null;
  specialty_ready_time?: Dayjs | null;
  specialty_ready_until_time?: Dayjs | null;
  specialty_delivery_ready_date?: Dayjs | null;
  specialty_delivery_ready_time?: Dayjs | null;
  specialty_delivery_until_time?: Dayjs | null;
  specialty_reference?: string;
  specialty_contents?: string;
  specialty_special_instructions?: string;
  specialty_delivery_instructions?: string;
  specialty_notify_email?: string;
  specialty_pieces?: SpecialtyFormPiece[];
  specialty_provider_fields?: Record<string, unknown>;
}

function trimStr(v: unknown): string | undefined {
  if (v == null) return undefined;
  const s = String(v).trim();
  return s || undefined;
}

function toParty(raw: SpecialtyFormParty | undefined): SpecialtyParty {
  return {
    name: trimStr(raw?.name),
    company: trimStr(raw?.company),
    phone: trimStr(raw?.phone),
    email: trimStr(raw?.email),
    street: trimStr(raw?.street),
    street2: trimStr(raw?.street2),
    city: trimStr(raw?.city),
    state: trimStr(raw?.state),
    postalCode: trimStr(raw?.postalCode),
    country: trimStr(raw?.country) || 'US',
    notes: trimStr(raw?.notes),
  };
}

function toNumber(v: unknown): number | undefined {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function toPieces(
  rows: SpecialtyFormPiece[] | undefined,
  requiresPieces: boolean
): SpecialtyPiece[] | undefined {
  if (!requiresPieces) return undefined;
  const pieces = (rows ?? [])
    .map((row): SpecialtyPiece | null => {
      const weight = toNumber(row.weight);
      if (weight == null || weight <= 0) return null;
      const length = toNumber(row.length);
      const width = toNumber(row.width);
      const height = toNumber(row.height);
      const quantityRaw = toNumber(row.quantity);
      const quantity =
        quantityRaw == null
          ? 1
          : Number.isInteger(quantityRaw) && quantityRaw >= 1
            ? quantityRaw
            : null;
      if (quantity == null) {
        throw new Error(
          'Package quantity must be a whole number of at least 1'
        );
      }
      return {
        weight,
        weightUnit: row.weightUnit === 'KG' ? 'KG' : 'LB',
        ...(length != null ? { length } : {}),
        ...(width != null ? { width } : {}),
        ...(height != null ? { height } : {}),
        dimensionUnit: row.dimensionUnit === 'CM' ? 'CM' : 'IN',
        quantity,
      };
    })
    .filter((p): p is SpecialtyPiece => p != null);
  if (requiresPieces && pieces.length === 0) {
    throw new Error('Enter package weight for at least one package');
  }
  return pieces;
}

export function mergeSpecialtyReadyAt(
  date: Dayjs | null | undefined,
  time: Dayjs | null | undefined
): string | undefined {
  if (!date || !dayjs.isDayjs(date)) return undefined;
  const hour = time && dayjs.isDayjs(time) ? time.hour() : 9;
  const minute = time && dayjs.isDayjs(time) ? time.minute() : 0;
  // Picked in the user's local time; the offset keeps both the instant and the
  // local wall-clock (Life Couriers books a local ready date and time).
  return dayjs(date.format('YYYY-MM-DD'))
    .hour(hour)
    .minute(minute)
    .second(0)
    .millisecond(0)
    .format('YYYY-MM-DDTHH:mm:ssZ');
}

export function buildSpecialtyBookPayload(
  provider: string,
  values: SpecialtyFormValues,
  requiresPieces: boolean
): SpecialtyBookPayload {
  const notify = trimStr(values.specialty_notify_email);
  const pieces = toPieces(values.specialty_pieces, requiresPieces);
  const providerFields = values.specialty_provider_fields ?? {};
  const readyAt = mergeSpecialtyReadyAt(
    values.specialty_ready_date,
    values.specialty_ready_time
  );
  const hasReadyUntil = dayjs.isDayjs(values.specialty_ready_until_time);
  const readyUntil = hasReadyUntil
    ? mergeSpecialtyReadyAt(
        values.specialty_ready_date,
        values.specialty_ready_until_time
      )
    : undefined;
  const deliveryDate =
    values.specialty_delivery_ready_date ?? values.specialty_ready_date;
  const hasDeliveryReady =
    dayjs.isDayjs(values.specialty_delivery_ready_date) ||
    dayjs.isDayjs(values.specialty_delivery_ready_time);
  const deliveryReadyAt = hasDeliveryReady
    ? mergeSpecialtyReadyAt(deliveryDate, values.specialty_delivery_ready_time)
    : undefined;
  const hasDeliveryUntil = dayjs.isDayjs(values.specialty_delivery_until_time);
  if (hasDeliveryUntil && !hasDeliveryReady) {
    throw new Error('Set delivery ready before delivery until');
  }
  const deliveryUntil = hasDeliveryUntil
    ? mergeSpecialtyReadyAt(deliveryDate, values.specialty_delivery_until_time)
    : undefined;
  const specialInstructions = trimStr(values.specialty_special_instructions);
  const deliveryInstructions = trimStr(values.specialty_delivery_instructions);
  return {
    provider,
    shipper: toParty(values.specialty_shipper),
    recipient: toParty(values.specialty_recipient),
    readyAt,
    ...(readyUntil ? { readyUntil } : {}),
    ...(deliveryReadyAt ? { deliveryReadyAt } : {}),
    ...(deliveryUntil ? { deliveryUntil } : {}),
    reference: trimStr(values.specialty_reference),
    contents: trimStr(values.specialty_contents),
    ...(specialInstructions ? { specialInstructions } : {}),
    ...(deliveryInstructions ? { deliveryInstructions } : {}),
    ...(notify ? { notifyEmails: [notify] } : {}),
    ...(pieces && pieces.length ? { pieces } : {}),
    providerFields,
  };
}

export function shellToSpecialtyFormValues(
  shipment: SingleShipmentDataExtended | undefined
): SpecialtyFormValues {
  const quote = shellToQuoteFormValues(shipment);
  const mapParty = (p: ShellPartyValues): SpecialtyFormParty => ({
    name: p.name,
    company: p.company,
    phone: p.phone,
    street: p.street,
    street2: p.street2,
    city: p.city,
    state: p.state,
    postalCode: p.postalCode,
    country: p.country || 'US',
  });
  return {
    specialty_shipper: mapParty(quote.pickup),
    specialty_recipient: mapParty(quote.delivery),
    specialty_reference: quote.reference,
    specialty_ready_date: quote.shipDate ?? dayjs(),
    specialty_ready_time: (quote.shipDate ?? dayjs()).hour(9).minute(0),
    specialty_pieces: [
      {
        weight: undefined,
        weightUnit: 'LB',
        length: undefined,
        width: undefined,
        height: undefined,
        dimensionUnit: 'IN',
        quantity: 1,
      },
    ],
    specialty_provider_fields: {},
  };
}

function mapOptionRows(raw: unknown): SpecialtyOptionChoice[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row): SpecialtyOptionChoice | null => {
      if (row == null || typeof row !== 'object') return null;
      const r = row as Record<string, unknown>;
      const value = r.value ?? r.id ?? r.code ?? r.name;
      if (value == null) return null;
      const label =
        (typeof r.label === 'string' && r.label) ||
        (typeof r.name === 'string' && r.name) ||
        String(value);
      return { value: value as string | number, label };
    })
    .filter((c): c is SpecialtyOptionChoice => c != null);
}

export function specialtyFieldChoices(
  options: SpecialtyOptionsResponse | null | undefined,
  field: SpecialtyOptionField,
  parentValue?: unknown
): SpecialtyOptionChoice[] {
  if (Array.isArray(field.options) && field.options.length) {
    return field.options;
  }
  if (
    field.optionsDependOn &&
    field.optionsParentKey &&
    field.optionsFromKey &&
    options
  ) {
    if (parentValue == null || parentValue === '') return [];
    const parentRows = options[field.optionsParentKey];
    if (!Array.isArray(parentRows)) return [];
    const selected = parentRows.find((row) => {
      if (row == null || typeof row !== 'object') return false;
      const r = row as Record<string, unknown>;
      const value = r.value ?? r.id ?? r.code ?? r.name;
      return value != null && String(value) === String(parentValue);
    }) as Record<string, unknown> | undefined;
    if (!selected) return [];
    return mapOptionRows(selected[field.optionsFromKey]);
  }
  const key = field.optionsKey;
  if (!key || !options) return [];
  return mapOptionRows(options[key]);
}

/** A carrier booking that succeeded but has not been saved as a shipment yet. */
export interface UnsavedSpecialtyBooking {
  trackingNumber: string;
  linkCode?: string;
  courierId: string;
  providerTitle: string;
  /** Shell shipment to promote; null means create a new shipment. */
  promoteTargetId: string | null;
  payload: SpecialtyBookPayload;
  /** Zoomlogi team ids for CreateNewShipment (not sent to the carrier). */
  teams?: string[];
  /** Zoomlogi products for CreateNewShipment (not sent to the carrier). */
  products?: Array<{
    product_id: string;
    quantity: number;
    serial_number?: string;
  }>;
}

const INCOMPATIBLE_TEMPS = /incompatible temperature ranges/i;

/** CreateNewShipment rejection when selected products' temperature ranges do not overlap. */
export const isIncompatibleProductTemperatureError = (
  formatted: string,
  err?: unknown
): boolean => {
  if (INCOMPATIBLE_TEMPS.test(formatted)) return true;
  if (!err || typeof err !== 'object') return false;
  const body =
    (err as { response?: { data?: unknown }; data?: unknown }).response?.data ??
    (err as { data?: unknown }).data;
  try {
    return INCOMPATIBLE_TEMPS.test(JSON.stringify(body ?? ''));
  } catch {
    return false;
  }
};

const unsavedBookingKey = (companyId: string) =>
  `zl:specialty-unsaved-booking:${companyId}`;

const isUnsavedBooking = (v: unknown): v is UnsavedSpecialtyBooking => {
  if (!v || typeof v !== 'object') return false;
  const b = v as Partial<UnsavedSpecialtyBooking>;
  return (
    typeof b.trackingNumber === 'string' &&
    typeof b.courierId === 'string' &&
    typeof b.providerTitle === 'string' &&
    (b.promoteTargetId === null || typeof b.promoteTargetId === 'string') &&
    !!b.payload &&
    typeof b.payload === 'object'
  );
};

/** Survives the drawer closing so the save can be retried without booking again. */
export function loadUnsavedSpecialtyBooking(
  companyId: string | null | undefined,
  storage: Pick<Storage, 'getItem'> = window.localStorage
): UnsavedSpecialtyBooking | null {
  if (!companyId) return null;
  try {
    const parsed: unknown = JSON.parse(
      storage.getItem(unsavedBookingKey(companyId)) ?? 'null'
    );
    return isUnsavedBooking(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function storeUnsavedSpecialtyBooking(
  companyId: string | null | undefined,
  booking: UnsavedSpecialtyBooking | null,
  storage: Pick<Storage, 'setItem' | 'removeItem'> = window.localStorage
): void {
  if (!companyId) return;
  try {
    if (booking) {
      storage.setItem(unsavedBookingKey(companyId), JSON.stringify(booking));
    } else {
      storage.removeItem(unsavedBookingKey(companyId));
    }
  } catch {
    // Storage can be full or disabled; the in-memory copy still allows a retry.
  }
}
